/**
 * Regattasiden — offentlig, uten pålogging.
 *
 * Viser regattanavn, starttid, og den synkroniserte nedtellingen.
 * Håndterer lydsignaler og flagg under startprosedyren.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type KomiteAction } from '../lib/api.ts';
import { syncTime, syncedNow } from '../lib/timesync.ts';
import {
  computeProcedure,
  detectAudioTriggers,
  type ProcedureState,
} from '../lib/startProcedure.ts';
import {
  unlockAudio,
  isAudioUnlocked,
  playShortBeep,
  playLongBeep,
} from '../lib/audio.ts';
import {
  enableWakeLock,
  disableWakeLock,
  isWakeLockSupported,
  isWakeLockActive,
} from '../lib/wakelock.ts';
import { BluePeterFlag, APFlag } from '../components/Flags.tsx';
import { SignalFlag } from '../components/SignalFlags.tsx';
import { SiteLayout } from '../components/SiteLayout.tsx';
import { RegisterPanel } from '../components/RegisterPanel.tsx';
import { FinishTiming } from '../components/FinishTiming.tsx';
import { RaceParticipant } from '../components/RaceParticipant.tsx';

export function RacePage() {
  const { slug } = useParams<{ slug: string }>();

  const raceQuery = useQuery({
    queryKey: ['race', slug],
    queryFn: () => api.race(slug!),
    enabled: !!slug,
    refetchInterval: 15_000, // Plukk opp utsettelse / statusendring
  });

  // Komitéstyrt start: klokka hentes hvert 2. sek fra et lett endepunkt, så alle
  // båter får komiteens varselsignal nesten samtidig. Komiteen får egne knapper.
  const committeeMode = raceQuery.data?.race.startMode === 'committee';
  const clockQuery = useQuery({
    queryKey: ['race-clock', slug],
    queryFn: () => api.raceClock(slug!),
    enabled: !!slug && committeeMode,
    refetchInterval: 2000,
  });
  const komiteQuery = useQuery({
    queryKey: ['race-komite', slug],
    queryFn: () => api.raceKomite(slug!),
    enabled: !!slug && committeeMode,
    staleTime: 60_000,
    retry: false,
  });
  const qc = useQueryClient();
  const [komiteBusy, setKomiteBusy] = useState(false);
  const [komiteErr, setKomiteErr] = useState<string | null>(null);

  const [offsetMs, setOffsetMs] = useState<number>(0);
  const [syncInfo, setSyncInfo] = useState<{ rtt: number; samples: number } | null>(null);
  const [state, setState] = useState<ProcedureState | null>(null);
  const [audioOn, setAudioOn] = useState<boolean>(isAudioUnlocked());
  const [minuteFlash, setMinuteFlash] = useState<boolean>(false);
  const [wakeLockOn, setWakeLockOn] = useState<boolean>(false);
  const [racePassword, setRacePassword] = useState(() => localStorage.getItem('rc_pw') ?? '');
  const [raceSail, setRaceSail] = useState(() => localStorage.getItem('rc_sail') ?? '');
  const prevMsRef = useRef<number | null>(null);

  // Manuell startklokke (regattaer der båtene starter klokka selv): målet for
  // nedtellingen settes fra knappene og huskes på enheten, så en omlasting midt
  // i prosedyren ikke nullstiller klokka. Eldre enn 3 timer = glemt, ignoreres.
  const manualKey = `rc_manual_start_${slug}`;
  const [manualStart, setManualStart] = useState<number | null>(() => {
    try {
      const v = Number(localStorage.getItem(manualKey));
      return Number.isFinite(v) && v > Date.now() - 3 * 3600_000 ? v : null;
    } catch {
      return null;
    }
  });
  function setManual(ms: number | null) {
    setManualStart(ms);
    try {
      if (ms == null) localStorage.removeItem(manualKey);
      else localStorage.setItem(manualKey, String(ms));
    } catch {
      /* privat modus o.l. */
    }
  }

  // Felles regatta-passord (deles av «Min seilas» og tidtaking) — huskes på enheten.
  const access = useQuery({
    queryKey: ['race-access', slug, raceSail, racePassword],
    queryFn: () => api.verifyRaceAccess(slug!, raceSail, racePassword),
    enabled: !!slug,
    // Feil passord (401/403) skal ikke prøves på nytt; nettverksfeil bør det.
    retry: (count, err) =>
      !(err instanceof ApiError && (err.status === 401 || err.status === 403)) && count < 2,
  });

  // Første tid-synkronisering ved lasting
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await syncTime(5);
      if (cancelled) return;
      setOffsetMs(r.offsetMs);
      setSyncInfo({ rtt: r.rttMs, samples: r.sampleCount });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resynkroniser hvert 60. sek
  useEffect(() => {
    const id = setInterval(async () => {
      const r = await syncTime(3);
      setOffsetMs(r.offsetMs);
      setSyncInfo({ rtt: r.rttMs, samples: r.sampleCount });
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Skjerm-på når lyd er aktivert og regattaen ikke er frosset.
  // Slippes automatisk når komponenten unmounter eller regattaen avsluttes.
  useEffect(() => {
    const race = raceQuery.data?.race;
    if (!race) return;
    const frozen = race.status === 'finished' || race.status === 'cancelled';
    const shouldLock = audioOn && !frozen;
    let cancelled = false;
    (async () => {
      if (shouldLock) {
        const ok = await enableWakeLock();
        if (!cancelled) setWakeLockOn(ok && isWakeLockActive());
      } else {
        await disableWakeLock();
        if (!cancelled) setWakeLockOn(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [audioOn, raceQuery.data?.race?.status]);

  // Sikkerhetsnett: frigi wake lock når siden forlates.
  useEffect(() => {
    return () => {
      void disableWakeLock();
    };
  }, []);

  // Utsettelse (AP) i manuell modus: klokka må startes på nytt ved nytt varselsignal.
  useEffect(() => {
    const race = raceQuery.data?.race;
    if (race?.startMode === 'manual' && race.status === 'postponed') setManual(null);
  }, [raceQuery.data?.race?.startMode, raceQuery.data?.race?.status]);

  // Hovedloop via requestAnimationFrame
  useEffect(() => {
    if (!raceQuery.data) return;
    const { race } = raceQuery.data;
    // Fast starttid: regattaens tid. Manuell: båtens egen klokke. Komité: komiteens
    // start (fra klokke-pollen). null = ikke startet ennå.
    const clock = race.startMode === 'committee' ? clockQuery.data : undefined;
    const startTimeMs =
      race.startMode === 'manual'
        ? manualStart
        : race.startMode === 'committee'
          ? clock?.committeeStarted
            ? new Date(clock.startTime).getTime()
            : null
          : new Date(race.startTime).getTime();
    const status = clock?.status ?? race.status;

    // Hvis regattaen er frosset (finished/cancelled), beregn én gang og stopp
    const frozenFromStatus = status === 'finished' || status === 'cancelled';
    if (frozenFromStatus) {
      const frozen = computeProcedure(0, status);
      setState(frozen);
      prevMsRef.current = null;
      return;
    }

    // Manuell/komité-klokke som ikke er startet: vent (eller AP ved utsettelse).
    if (startTimeMs == null) {
      const waiting = computeProcedure(10 * 60_000, status);
      setState({ ...waiting, countdownText: status === 'postponed' ? 'UTSATT' : '–:––' });
      prevMsRef.current = null;
      return;
    }

    let raf = 0;
    const loop = () => {
      const now = syncedNow(offsetMs);
      const msToStart = startTimeMs - now;
      const newState = computeProcedure(msToStart, status);

      // Varselsignalet kom akkurat inn (komiteen eller båten startet klokka): ett pip.
      if (audioOn && prevMsRef.current === null && msToStart <= 300_500 && msToStart > 285_000) {
        playShortBeep();
      }

      // Lydsignaler: sammenlign forrige tick med nåværende. Fyr KUN ved en jevn,
      // kontinuerlig nedtelling (lite skritt). Ved en diskontinuitet — justert
      // starttid, tidssynk-korreksjon, eller skjerm/fane som var vekk og kommer
      // tilbake — ville et byks ellers fyrt av en salve falske signaler samtidig.
      // Da hopper vi over signalene for denne tikken og setter kun ny baseline.
      if (audioOn && prevMsRef.current !== null) {
        const step = prevMsRef.current - msToStart; // normal nedtelling: liten positiv verdi
        if (step > 0 && step < 2000) {
          const triggers = detectAudioTriggers(prevMsRef.current, msToStart, status);
          for (const t of triggers) {
            if (t.kind === 'long') playLongBeep();
            else playShortBeep();
          }
          // Visuell minutt-flash ved hver minutt-markering
          if (triggers.some((t) => t.reason.includes('min') || t.reason.includes('Varselssignal'))) {
            setMinuteFlash(true);
            setTimeout(() => setMinuteFlash(false), 500);
          }
        }
      }

      prevMsRef.current = msToStart;
      setState(newState);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [raceQuery.data, clockQuery.data, offsetMs, audioOn, manualStart]);

  if (raceQuery.isLoading) {
    return (
      <SiteLayout>
        <div className="py-24 text-center text-slate-400">Laster regatta…</div>
      </SiteLayout>
    );
  }

  if (raceQuery.isError || !raceQuery.data) {
    return (
      <SiteLayout>
        <div className="flex flex-col items-center py-24 px-6 text-center">
          <p className="text-red-400 text-lg mb-4">Klarte ikke laste regattaen.</p>
          <Link to="/" className="text-sky-400 underline">Tilbake til forsiden</Link>
        </div>
      </SiteLayout>
    );
  }

  const { race, forening, classes } = raceQuery.data;

  // Tilgang gjelder kun deltakerdelen («Min seilas» og nedover). Selve
  // startprosedyren (klokke, flagg, nedtelling) vises offentlig, uten innlogging.
  const canParticipate = access.isSuccess;
  const accessStatus = access.error instanceof ApiError ? access.error.status : null;

  // Logg ut båten (glemmer båtnummer/passord på enheten).
  function boatLogout() {
    localStorage.removeItem('rc_sail');
    localStorage.removeItem('rc_pw');
    setRaceSail('');
    setRacePassword('');
  }

  const startTimeMs = new Date(race.startTime).getTime();
  const manual = race.startMode === 'manual';
  const committee = race.startMode === 'committee';
  const clock = committee ? clockQuery.data : undefined;
  const committeeStarted = !!clock?.committeeStarted;
  const clockStatus = clock?.status ?? race.status;
  const postponeReason = clock ? clock.postponeReason : race.postponeReason;
  const isKomite = committee && !!komiteQuery.data?.allowed;
  // Tidtaking regner tid fra klokka på siden: båtens egen start (manuell) eller komiteens.
  const effectiveStartMs = manual
    ? (manualStart ?? startTimeMs)
    : committeeStarted
      ? new Date(clock!.startTime).getTime()
      : startTimeMs;
  async function startManual(minutes: number) {
    if (!audioOn) setAudioOn(await unlockAudio());
    setManual(syncedNow(offsetMs) + minutes * 60_000);
  }
  // Komiteens knapper. Svaret er den nye klokka, som vises med en gang.
  async function komite(body: KomiteAction, confirmText?: string) {
    if (!slug || komiteBusy) return;
    if (confirmText && !window.confirm(confirmText)) return;
    if (!audioOn) setAudioOn(await unlockAudio());
    setKomiteBusy(true);
    setKomiteErr(null);
    try {
      qc.setQueryData(['race-clock', slug], await api.raceKomiteAction(slug, body));
    } catch (e) {
      setKomiteErr(e instanceof ApiError ? e.message : 'Kunne ikke nå serveren — prøv igjen');
    } finally {
      setKomiteBusy(false);
    }
  }
  const startLabel = new Date(race.startTime).toLocaleString('no-NO', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Oslo',
  });
  const phase = state?.phase;
  const started = phase === 'started';
  const showFlags = !!state && !started && phase !== 'finished' && phase !== 'cancelled';

  const bgClass = state
    ? state.phase === 'finished'
      ? 'bg-slate-900'
      : state.phase === 'cancelled'
      ? 'bg-red-950/40'
      : state.apFlagUp
      ? 'bg-amber-900'
      : state.phase === 'last_10s'
      ? 'last10-pulse'
      : state.phase === 'started'
      ? 'bg-emerald-950'
      : state.phase === 'one_minute'
      ? 'bg-amber-950'
      : state.phase === 'preparatory' || state.phase === 'warning'
      ? 'bg-yellow-900/40'
      : 'bg-slate-900'
    : 'bg-slate-900';

  return (
    <SiteLayout bg={`transition-colors duration-500 ${bgClass}`}>
      <div className="max-w-xl mx-auto px-4 pt-4 pb-10">
        {/* Regattanavn sentrert, arrangørklubbens logo til høyre */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-16 shrink-0" />
          <div className="min-w-0 flex-1 text-center">
            <h1 className="text-lg md:text-2xl font-bold leading-tight">{race.name}</h1>
            {race.venue && (
              <div className="text-xs md:text-sm text-slate-400 leading-tight">{race.venue}</div>
            )}
          </div>
          {forening?.hasLogo ? (
            <img
              src={`/api/foreninger/${forening.slug}/logo`}
              alt={forening.name}
              className="w-16 h-16 rounded-full bg-white/95 p-1 object-contain shadow-lg shadow-black/30 shrink-0"
            />
          ) : (
            <div className="w-16 shrink-0" />
          )}
        </div>
        {state?.apFlagUp && (
          <div className="text-sm text-amber-300 font-semibold mb-4 text-center">
            ⚠️ Regattaen er utsatt
            {postponeReason && <span className="font-normal"> — {postponeReason}</span>}
          </div>
        )}

        {/* Nedtelling */}
        <div
          className={`
            rounded-2xl bg-slate-900/60 border border-slate-700 p-6 text-center mb-6
            ${minuteFlash ? 'minute-flash' : ''}
          `}
        >
          <div
            className="font-mono font-bold tabular-nums tracking-tight"
            style={{ fontSize: 'clamp(3.5rem, 18vw, 7rem)', lineHeight: 1 }}
          >
            {state?.countdownText ?? '—'}
          </div>
          <div className="mt-2 text-sm text-slate-400">
            {state?.phase === 'finished'
              ? 'Regattaen er avsluttet'
              : state?.phase === 'cancelled'
              ? 'Regattaen er avlyst'
              : state?.apFlagUp
              ? 'venter på ny starttid'
              : manual && manualStart == null
              ? 'Start klokka når komiteens signal går'
              : committee && !committeeStarted
              ? 'Venter på komiteens varselsignal'
              : state?.isBeforeStart
              ? 'til start'
              : 'etter start'}{' '}
            {manual ? (
              <span className="text-slate-300">(egen startklokke)</span>
            ) : committee ? (
              <span className="text-slate-300">(komitéstyrt start)</span>
            ) : (
              <span className="font-mono text-slate-300">({startLabel})</span>
            )}
          </div>
          {/* Manuell modus: båten starter klokka selv på komiteens signaler. */}
          {manual && !state?.isFrozen && (
            <>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {[5, 4, 1].map((m) => (
                  <button
                    key={m}
                    onClick={() => void startManual(m)}
                    className="min-w-[4.5rem] px-4 py-2.5 rounded-lg bg-sky-700 hover:bg-sky-600 font-bold"
                  >
                    {m} min
                  </button>
                ))}
                {manualStart != null && (
                  <button
                    onClick={() => setManual(null)}
                    className="px-4 py-2.5 rounded-lg border border-red-900 text-red-300 hover:bg-red-950/40 font-semibold"
                  >
                    Nullstill
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Trykk når signalet går: 5 min = varselsignal, 4 min = klarsignal, 1 min = ett minutt.
              </p>
            </>
          )}
        </div>

        {/* Komitéstyrt start: komiteens knapper (kun for innlogget komité). */}
        {isKomite && clockStatus !== 'finished' && clockStatus !== 'cancelled' && (
          <div className="rounded-2xl border border-amber-600/60 bg-amber-950/40 p-4 mb-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Komité</span>
              <span className="text-xs text-slate-400">Alle båter følger denne klokka</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {clockStatus === 'postponed' && (
                <button
                  onClick={() => void komite({ action: 'gjenoppta' })}
                  disabled={komiteBusy}
                  className="px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 font-bold disabled:opacity-50"
                >
                  AP ned
                </button>
              )}
              <button
                onClick={() =>
                  void komite(
                    { action: 'start', minutes: 5 },
                    committeeStarted ? 'Starte prosedyren på nytt fra 5 minutter for alle båter?' : undefined,
                  )
                }
                disabled={komiteBusy}
                className="px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 font-bold disabled:opacity-50"
              >
                {committeeStarted ? 'Ny start (5 min)' : 'Varselsignal nå (5 min)'}
              </button>
              {committeeStarted && (
                <>
                  <button
                    onClick={() => void komite({ action: 'start', minutes: 4 })}
                    disabled={komiteBusy}
                    className="px-4 py-2.5 rounded-lg bg-sky-700 hover:bg-sky-600 font-bold disabled:opacity-50"
                  >
                    Juster: 4 min
                  </button>
                  <button
                    onClick={() => void komite({ action: 'start', minutes: 1 })}
                    disabled={komiteBusy}
                    className="px-4 py-2.5 rounded-lg bg-sky-700 hover:bg-sky-600 font-bold disabled:opacity-50"
                  >
                    Juster: 1 min
                  </button>
                </>
              )}
              {clockStatus !== 'postponed' && (
                <button
                  onClick={() =>
                    void komite({ action: 'ap' }, 'Heise AP og utsette starten? Klokka stopper for alle båter.')
                  }
                  disabled={komiteBusy}
                  className="px-4 py-2.5 rounded-lg bg-amber-700 hover:bg-amber-600 font-bold disabled:opacity-50"
                >
                  AP — utsett
                </button>
              )}
              {committeeStarted && (
                <button
                  onClick={() => void komite({ action: 'nullstill' }, 'Nullstille klokka for alle båter?')}
                  disabled={komiteBusy}
                  className="px-4 py-2.5 rounded-lg border border-red-900 text-red-300 hover:bg-red-950/40 font-semibold disabled:opacity-50"
                >
                  Nullstill
                </button>
              )}
            </div>
            {komiteErr && <p className="mt-2 text-sm text-red-300">{komiteErr}</p>}
            <p className="mt-2 text-xs text-slate-400">
              Båtene får klokka innen et par sekunder. Etter «AP ned» gis nytt varselsignal med «5 min».
            </p>
          </div>
        )}

        {/* Flagg + lyd på samme linje (sparer plass). Flaggene vises før start,
            lyd-kontrollen så lenge regattaen ikke er frosset. */}
        {(showFlags || !state?.isFrozen) && (
          <div className="flex flex-wrap items-end justify-center gap-4 mb-6">
            {showFlags && (
              <>
                {(classes.length ? classes : [{ code: 'G', name: 'Klasseflagg', flag: 'G' }]).map((c) => (
                  <SignalFlag key={c.code} code={c.flag} label={c.name} up={state?.classFlagUp ?? false} />
                ))}
                <BluePeterFlag up={state?.bluePeterUp ?? false} />
                {state?.apFlagUp && <APFlag up={true} />}
              </>
            )}
            {!state?.isFrozen &&
              (!audioOn ? (
                <button
                  onClick={async () => {
                    const ok = await unlockAudio();
                    setAudioOn(ok);
                  }}
                  title="Aktiver lyd for startsignaler"
                  className="px-4 py-2 bg-sky-700 hover:bg-sky-600 rounded-lg text-sm font-semibold"
                >
                  🔔 Aktiver lyd
                </button>
              ) : (
                <div
                  className="text-xs text-slate-500 pb-1 whitespace-nowrap"
                  title={
                    wakeLockOn
                      ? 'Lyd aktivert · skjermen holdes på'
                      : !isWakeLockSupported()
                      ? 'Lyd aktivert · skjermen kan slukke (ikke støttet på denne enheten)'
                      : 'Lyd aktivert'
                  }
                >
                  🔔 Lyd på
                  {wakeLockOn && <span className="ml-1">· 📱</span>}
                  {!wakeLockOn && !isWakeLockSupported() && (
                    <span className="ml-1 text-amber-400">· ⚠️</span>
                  )}
                </div>
              ))}
          </div>
        )}
        {/* Deltakerdelen krever båt-innlogging; startprosedyren over er offentlig. */}
        {canParticipate ? (
          <>
        {access.data?.gated && (
          <div className="text-xs text-slate-400 text-center mb-4">
            Logget inn som <span className="text-slate-200">{access.data.boatName}</span>{' '}
            <span className="font-mono">({access.data.sailNumber})</span> ·{' '}
            <button onClick={boatLogout} className="underline hover:text-slate-200">
              Logg ut
            </button>
          </div>
        )}
        {/* Min seilas: fart/kurs + send posisjon + kart (ny fane) */}
        <RaceParticipant slug={race.slug} sail={raceSail} password={racePassword} />

        {/* Tidtaking / målgang */}
        <FinishTiming
          slug={race.slug}
          startTimeMs={effectiveStartMs}
          offsetMs={offsetMs}
          sail={raceSail}
          password={racePassword}
        />
          </>
        ) : access.isPending || accessStatus === 403 ? (
          <div className="rounded-2xl bg-slate-900/60 border border-slate-700 p-5 text-center mb-6">
            {access.isPending ? (
              <p className="text-sm text-slate-400">Sjekker tilgang…</p>
            ) : (
              <>
                <p className="text-amber-300 font-semibold mb-1">
                  🚫 {access.error instanceof ApiError ? access.error.message : 'Ikke kvalifisert'}
                </p>
                <p className="text-sm text-slate-400">
                  Regattaen er åpen kun for bestemte klasser. Sjekk båtens klasse i registeret.
                </p>
                <button
                  onClick={boatLogout}
                  className="mt-3 text-xs text-sky-400 hover:text-sky-300 underline"
                >
                  Logg ut / bytt båt
                </button>
              </>
            )}
          </div>
        ) : (
          // Båt-innlogging her, der den trengs (forsiden har ikke lenger innlogging).
          <RegisterPanel
            forening={forening?.slug}
            onLogin={(sail, password) => {
              setRaceSail(sail);
              setRacePassword(password);
            }}
          />
        )}

        {/* Sync-status */}
        <div className="text-xs text-slate-500 text-center">
          {syncInfo ? (
            <>
              Synkronisert • ±{syncInfo.rtt} ms • {syncInfo.samples} målinger
              {Math.abs(offsetMs) > 2000 && (
                <span className="text-amber-400 ml-2">
                  (telefonklokken din avviker {Math.round(offsetMs / 1000)}s fra server — bekreftet korrigert)
                </span>
              )}
            </>
          ) : (
            'Synkroniserer…'
          )}
        </div>
      </div>
    </SiteLayout>
  );
}
